import { useEffect, useRef, useState } from "react";
import CabeceraPagina from "../components/CabeceraPagina.jsx";
import { CAMBIOS } from "../data/novedades.js";
import { payloadToken, esSuperAdmin, urlApiConSesion } from "../lib/sesion.js";

const TIPOS = {
  nuevo: { etiqueta: "Nuevo", clases: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  mejora: { etiqueta: "Mejora", clases: "bg-sky-100 text-sky-700 border-sky-200" },
  correccion: { etiqueta: "Corrección", clases: "bg-amber-100 text-amber-700 border-amber-200" },
  seguridad: { etiqueta: "Seguridad", clases: "bg-red-100 text-red-700 border-red-200" },
};

const ESTADOS = {
  pendiente: { etiqueta: "Pendiente", clases: "bg-amber-100 text-amber-700 border-amber-200" },
  realizada: { etiqueta: "Realizada", clases: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  descartada: { etiqueta: "Descartada", clases: "bg-slate-100 text-slate-500 border-slate-200" },
};

function fechaTxt(iso) {
  return new Date(iso).toLocaleDateString("es-ES", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function Chip({ texto, clases }) {
  return (
    <span className={`shrink-0 text-[0.6875rem] font-semibold px-2 py-0.5 rounded-full border ${clases}`}>
      {texto}
    </span>
  );
}

export default function NovedadesPage() {
  const [vista, setVista] = useState("cambios");
  const [propuestas, setPropuestas] = useState(null);
  const [filtroEstado, setFiltroEstado] = useState("pendiente");
  const [archivando, setArchivando] = useState(null);
  const [texto, setTexto] = useState("");
  const [capturas, setCapturas] = useState([]); // File[] con su URL de vista previa
  const [enviando, setEnviando] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [error, setError] = useState(null);
  const inputCapturasRef = useRef(null);
  const usuario = payloadToken()?.nombre ?? "";
  // El superadministrador ve las propuestas de TODAS las empresas juntas.
  const superadmin = esSuperAdmin();

  useEffect(() => {
    fetch(superadmin ? "/api/propuestas/todas" : "/api/propuestas")
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("No se pudieron cargar"))))
      .then(setPropuestas)
      .catch((e) => setError(e.message));
  }, [superadmin]);

  // El superadministrador archiva propuestas (realizada/descartada) o las reabre.
  // Al archivar puede escribir una respuesta para el autor: se guarda en su
  // buzón y se le intenta enviar por correo con el SMTP de su empresa.
  const [respondiendo, setRespondiendo] = useState(null); // { id, estado, texto }
  const [avisoRespuesta, setAvisoRespuesta] = useState(null);

  function abrirRespuesta(p, estado) {
    setRespondiendo({ id: p._id, estado, texto: "" });
  }

  async function marcarEstado(p, estado, respuesta = "") {
    setArchivando(p._id);
    setError(null);
    try {
      const r = await fetch(`/api/propuestas/todas/${p.empresaSlug}/${p._id}/estado`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ estado, respuesta }),
      });
      const datos = await r.json();
      if (!r.ok) throw new Error(datos.error || "No se pudo actualizar");
      setPropuestas((ps) => ps.map((x) => (x._id === p._id ? { ...x, ...datos } : x)));
      if (estado !== "pendiente" && respuesta) {
        setAvisoRespuesta(
          datos.correoEnviado
            ? "Respuesta guardada y enviada por correo al autor."
            : "Respuesta guardada en el buzón del autor. No se pudo enviar por correo (su empresa no tiene el correo configurado o falló el envío)."
        );
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setArchivando(null);
      setRespondiendo(null);
    }
  }

  const pendientes = (propuestas ?? []).filter((p) => (p.estado ?? "pendiente") === "pendiente").length;
  const propuestasVisibles = superadmin
    ? (propuestas ?? []).filter((p) => (p.estado ?? "pendiente") === filtroEstado)
    : (propuestas ?? []);

  function anadirCapturas(e) {
    const nuevas = Array.from(e.target.files ?? []).filter((f) => f.type.startsWith("image/"));
    e.target.value = "";
    if (!nuevas.length) return;
    setCapturas((cs) =>
      [...cs, ...nuevas.map((archivo) => ({ archivo, vista: URL.createObjectURL(archivo) }))].slice(0, 4)
    );
  }

  function quitarCaptura(i) {
    setCapturas((cs) => {
      URL.revokeObjectURL(cs[i]?.vista);
      return cs.filter((_, j) => j !== i);
    });
  }

  async function enviar(e) {
    e.preventDefault();
    if (!texto.trim()) return;
    setEnviando(true);
    setError(null);
    setAviso(null);
    try {
      const fd = new FormData();
      fd.append("texto", texto);
      capturas.forEach((c) => fd.append("capturas", c.archivo));
      const r = await fetch("/api/propuestas", { method: "POST", body: fd });
      if (!r.ok) throw new Error((await r.json()).error || "No se pudo enviar");
      const creada = await r.json();
      setPropuestas((ps) => [creada, ...(ps ?? [])]);
      setTexto("");
      capturas.forEach((c) => URL.revokeObjectURL(c.vista));
      setCapturas([]);
      setAviso("Propuesta enviada. Gracias por ayudarnos a mejorar.");
    } catch (e2) {
      setError(e2.message);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <>
      <CabeceraPagina
        titulo="Novedades del programa"
        descripcion="Registro de mejoras y cambios, y buzón de propuestas de los usuarios."
      />

      <div className="max-w-4xl space-y-4">
        <div className="flex gap-2">
          <button
            onClick={() => setVista("cambios")}
            className={vista === "cambios" ? "btn-primary !py-1.5 text-sm" : "btn-ghost !py-1.5 text-sm"}
          >
            Cambios realizados ({CAMBIOS.length})
          </button>
          <button
            onClick={() => setVista("propuestas")}
            className={vista === "propuestas" ? "btn-primary !py-1.5 text-sm" : "btn-ghost !py-1.5 text-sm"}
          >
            Propuestas{propuestas ? (superadmin ? ` (${pendientes} pendientes)` : ` (${propuestas.length})`) : ""}
          </button>
        </div>

        {vista === "cambios" && (
          <div className="space-y-3">
            {CAMBIOS.map((c, i) => {
              const tipo = TIPOS[c.tipo] ?? TIPOS.mejora;
              return (
                <div key={i} className="panel p-4 flex gap-4">
                  <div className="shrink-0 w-28 text-xs text-slate-500 pt-0.5">
                    {fechaTxt(`${c.fecha}T12:00:00`)}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Chip texto={tipo.etiqueta} clases={tipo.clases} />
                      <h3 className="font-semibold text-slate-800">{c.titulo}</h3>
                    </div>
                    <p className="text-sm text-slate-600 mt-1 leading-relaxed">{c.detalle}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {vista === "propuestas" && (
          <div className="space-y-4">
            <form onSubmit={enviar} className="panel p-4 space-y-3">
              <div>
                <h3 className="font-semibold text-slate-800">¿Qué te gustaría que hiciera el programa?</h3>
                <p className="text-sm text-slate-500 mt-0.5">
                  Escribe tu idea o mejora. La leemos y la tenemos en cuenta para las próximas versiones.
                </p>
              </div>
              <textarea
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                rows={3}
                maxLength={1000}
                placeholder="Ej.: Me gustaría poder exportar el listado de clientes a Excel…"
                className="input w-full"
              />
              <input
                ref={inputCapturasRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={anadirCapturas}
              />
              {capturas.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {capturas.map((c, i) => (
                    <span key={c.vista} className="relative inline-block">
                      <img
                        src={c.vista}
                        alt={`Captura ${i + 1}`}
                        className="h-16 w-16 object-cover rounded-lg border border-slate-200"
                      />
                      <button
                        type="button"
                        onClick={() => quitarCaptura(i)}
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-slate-700 text-white text-xs leading-none hover:bg-red-500"
                        title="Quitar captura"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              {error && <p className="text-sm text-red-500">{error}</p>}
              {aviso && <p className="text-sm text-emerald-600">{aviso}</p>}
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <button type="button" onClick={() => inputCapturasRef.current?.click()} className="btn-ghost !py-1.5 text-sm">
                    Adjuntar captura{capturas.length > 0 ? ` (${capturas.length}/4)` : ""}
                  </button>
                  <p className="text-xs text-slate-500">
                    Se envía como <span className="font-semibold text-slate-600">{usuario || "usuario actual"}</span>
                  </p>
                </div>
                <button type="submit" disabled={enviando || !texto.trim()} className="btn-primary">
                  {enviando ? "Enviando…" : "Enviar propuesta"}
                </button>
              </div>
            </form>

            <div className="space-y-3">
              {superadmin && (
                <div className="flex gap-2 flex-wrap">
                  {Object.entries(ESTADOS).map(([clave, info]) => {
                    const n = (propuestas ?? []).filter((p) => (p.estado ?? "pendiente") === clave).length;
                    return (
                      <button
                        key={clave}
                        onClick={() => setFiltroEstado(clave)}
                        className={
                          filtroEstado === clave
                            ? "btn-primary !py-1 !px-3 text-xs"
                            : "btn-ghost !py-1 !px-3 text-xs"
                        }
                      >
                        {info.etiqueta}s ({n})
                      </button>
                    );
                  })}
                </div>
              )}
              {superadmin && avisoRespuesta && (
                <p className="text-sm text-emerald-600">{avisoRespuesta}</p>
              )}
              {propuestas === null && !error && (
                <p className="text-sm text-slate-500">Cargando propuestas…</p>
              )}
              {propuestasVisibles.length === 0 && propuestas !== null && (
                <p className="text-sm text-slate-500">
                  {superadmin
                    ? `No hay propuestas ${ESTADOS[filtroEstado].etiqueta.toLowerCase()}s.`
                    : "Todavía no hay propuestas. Sé el primero en escribir una."}
                </p>
              )}
              {propuestasVisibles.map((p) => {
                const estadoInfo = ESTADOS[p.estado ?? "pendiente"] ?? ESTADOS.pendiente;
                return (
                <div key={p._id} className="panel p-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-800 text-sm">
                      {p.usuarioNombre || p.usuarioEmail || "Usuario"}
                    </span>
                    {superadmin && p.empresaNombre && (
                      <Chip
                        texto={p.empresaNombre}
                        clases="bg-violet-100 text-violet-700 border-violet-200"
                      />
                    )}
                    <Chip texto={estadoInfo.etiqueta} clases={estadoInfo.clases} />
                    <span className="text-xs text-slate-500">{fechaTxt(p.createdAt)}</span>
                  </div>
                  <p className="text-sm text-slate-600 mt-1 leading-relaxed whitespace-pre-line">{p.texto}</p>
                  {p.respuesta && (
                    <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                      <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide">
                        Respuesta del equipo FILANEX
                        {p.respuestaFecha && (
                          <span className="normal-case font-normal text-emerald-600"> · {fechaTxt(p.respuestaFecha)}</span>
                        )}
                      </p>
                      <p className="text-sm text-slate-700 mt-1 leading-relaxed whitespace-pre-line">{p.respuesta}</p>
                      {p.respondidaPor && (
                        <p className="text-xs text-emerald-600 mt-1.5">— {p.respondidaPor}</p>
                      )}
                    </div>
                  )}
                  {p.adjuntos?.length > 0 && (
                    <div className="flex gap-2 flex-wrap mt-2">
                      {p.adjuntos.map((a, j) => (
                        <a key={a.url} href={urlApiConSesion(a.url)} target="_blank" rel="noreferrer" title={a.nombre || "Ver captura"}>
                          <img
                            src={urlApiConSesion(a.url)}
                            alt={a.nombre || `Captura ${j + 1}`}
                            className="h-16 w-16 object-cover rounded-lg border border-slate-200 hover:border-accent hover:opacity-80 transition-all"
                          />
                        </a>
                      ))}
                    </div>
                  )}
                  {superadmin && (
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      {respondiendo?.id === p._id ? (
                        <div className="space-y-2">
                          <p className="text-xs font-semibold text-slate-600">
                            Respuesta para {p.usuarioNombre || p.usuarioEmail || "el autor"}
                            {respondiendo.estado === "descartada"
                              ? " (explica por qué se descarta, para que no quede a ciegas)"
                              : " (cuenta qué se ha hecho y cómo usarlo)"}
                            :
                          </p>
                          <textarea
                            autoFocus
                            value={respondiendo.texto}
                            onChange={(e) => setRespondiendo((r) => ({ ...r, texto: e.target.value }))}
                            rows={3}
                            maxLength={1500}
                            placeholder="Ej.: Ya está arreglado: el buscador de clientes ahora carga toda la cartera…"
                            className="input w-full"
                          />
                          <div className="flex items-center gap-2 flex-wrap">
                            <button
                              onClick={() => marcarEstado(p, respondiendo.estado, respondiendo.texto)}
                              disabled={archivando === p._id || !respondiendo.texto.trim()}
                              className="btn-primary !py-1.5 !px-3 text-xs"
                            >
                              {archivando === p._id
                                ? "Guardando…"
                                : respondiendo.estado === "realizada"
                                  ? "✓ Marcar realizada y responder"
                                  : "✕ Descartar y responder"}
                            </button>
                            <button
                              onClick={() => marcarEstado(p, respondiendo.estado)}
                              disabled={archivando === p._id}
                              className="btn-ghost !py-1.5 !px-3 text-xs"
                            >
                              Archivar sin respuesta
                            </button>
                            <button
                              onClick={() => setRespondiendo(null)}
                              className="text-xs text-slate-500 hover:text-slate-700"
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex gap-2 flex-wrap">
                          {(p.estado ?? "pendiente") !== "realizada" && (
                            <button
                              onClick={() => abrirRespuesta(p, "realizada")}
                              disabled={archivando === p._id}
                              className="btn-ghost !py-1 !px-2.5 text-xs !text-emerald-600 hover:!bg-emerald-50 disabled:opacity-50"
                            >
                              ✓ Marcar realizada
                            </button>
                          )}
                          {(p.estado ?? "pendiente") !== "descartada" && (
                            <button
                              onClick={() => abrirRespuesta(p, "descartada")}
                              disabled={archivando === p._id}
                              className="btn-ghost !py-1 !px-2.5 text-xs !text-slate-500 hover:!bg-slate-100 disabled:opacity-50"
                            >
                              ✕ Descartar
                            </button>
                          )}
                          {(p.estado ?? "pendiente") !== "pendiente" && (
                            <button
                              onClick={() => marcarEstado(p, "pendiente")}
                              disabled={archivando === p._id}
                              className="btn-ghost !py-1 !px-2.5 text-xs !text-amber-600 hover:!bg-amber-50 disabled:opacity-50"
                            >
                              ↩ Reabrir
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
